import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/components/ui/LanguageContext";
import { motion } from "framer-motion";
import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FolderTree,
  Tags,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";
import VideoCard from "@/components/common/VideoCard";
import { Skeleton } from "@/components/ui/skeleton";
import { getCatalogSummary, listAllVideos, listUserProgress, listVideos } from "@/api/content";
import { useRequireAuthRedirect } from "@/hooks/useRequireAuthRedirect";
import {
  SERIES_FILTER_DICTIONARY,
  SERIES_FILTER_HIDDEN,
  SERIES_FILTER_ORDER,
  TAG_FILTER_DICTIONARY,
  TAG_FILTER_GROUPS,
  SORT_OPTIONS,
  getDictionaryLabel,
} from "@/config/catalogFilters";

const CATALOG_PAGE_SIZE = 15;
// カテゴリページの並び順。`videos.sort_order` の降順 / 昇順を切り替えるだけで、
// 値そのものは admin の並び替え機能が決めたものをそのまま使う。
const CATEGORY_ORDER_DEFAULT = "desc";

function getBoolSearchParam(params, key) {
  const value = params.get(key);
  return value === "1" || value === "true";
}

function getPositiveIntSearchParam(params, key, fallback) {
  const value = Number(params.get(key));
  return Number.isInteger(value) && value > 0 ? value : fallback;
}

function getCsvSearchParam(params, key) {
  return String(params.get(key) || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

function readCatalogFilters(params) {
  const sortValue = params.get("sort") || "newest";
  const showWatched = getBoolSearchParam(params, "watched");

  return {
    filterMode: params.get("mode") === "tag" ? "tag" : "category",
    seriesLikeCategoryFilter: params.get("category") || "all",
    tagCategoryFilter: params.get("tagCategory") || "all",
    selectedTagFilters: getCsvSearchParam(params, "tags"),
    sort: SORT_OPTIONS.some((option) => option.value === sortValue) ? sortValue : "newest",
    categoryOrder: params.get("order") === "asc" ? "asc" : CATEGORY_ORDER_DEFAULT,
    showUnwatched: !showWatched && getBoolSearchParam(params, "unwatched"),
    showWatched,
    currentPage: getPositiveIntSearchParam(params, "page", 1),
  };
}

function buildCatalogSearch(filters) {
  const params = new URLSearchParams();

  if (filters.filterMode !== "category") params.set("mode", filters.filterMode);
  if (filters.seriesLikeCategoryFilter !== "all") params.set("category", filters.seriesLikeCategoryFilter);
  if (filters.tagCategoryFilter !== "all") params.set("tagCategory", filters.tagCategoryFilter);
  if (filters.selectedTagFilters.length > 0) params.set("tags", filters.selectedTagFilters.join(","));
  if (filters.sort !== "newest") params.set("sort", filters.sort);
  if (filters.categoryOrder !== CATEGORY_ORDER_DEFAULT) params.set("order", filters.categoryOrder);
  if (filters.showUnwatched) params.set("unwatched", "1");
  if (filters.showWatched) params.set("watched", "1");
  if (filters.currentPage > 1) params.set("page", String(filters.currentPage));

  return params.toString();
}

function getVideoTagSlugs(video) {
  return (Array.isArray(video.tags) ? video.tags : [])
    .map((entry) => (typeof entry === "string" ? entry : entry?.slug))
    .filter(Boolean);
}

function getSummaryCategoryId(item) {
  return item?.categoryId ?? item?.category_id ?? item?.id ?? null;
}

export default function Catalog() {
  const { t, language } = useLanguage();
  const { canRenderProtectedContent, isLoadingAuth } = useRequireAuthRedirect();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialFilters] = useState(() => readCatalogFilters(searchParams));
  const [filterMode, setFilterMode] = useState(initialFilters.filterMode);
  const [seriesLikeCategoryFilter, setSeriesLikeCategoryFilter] = useState(initialFilters.seriesLikeCategoryFilter);
  const [tagCategoryFilter, setTagCategoryFilter] = useState(initialFilters.tagCategoryFilter);
  const [selectedTagFilters, setSelectedTagFilters] = useState(initialFilters.selectedTagFilters);
  const [sort, setSort] = useState(initialFilters.sort);
  const [categoryOrder, setCategoryOrder] = useState(initialFilters.categoryOrder);
  const [showUnwatched, setShowUnwatched] = useState(initialFilters.showUnwatched);
  const [showWatched, setShowWatched] = useState(initialFilters.showWatched);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialFilters.currentPage);
  const videoColumnRef = useRef(null);
  const hasMountedFilterResetRef = useRef(false);
  // 自分で書き込んだ検索文字列。ブラウザバック由来の変化と区別するために持つ。
  const writtenSearchRef = useRef(searchParams.toString());
  const previousCategoryRef = useRef(initialFilters.seriesLikeCategoryFilter);
  const isRestoringFiltersRef = useRef(false);
  // カテゴリ選択で自前の履歴を積んだかどうか。積んでいれば「戻る」は履歴を1つ戻す。
  const hasPushedCategoryEntryRef = useRef(false);
  const [tagPanelMaxHeight, setTagPanelMaxHeight] = useState(null);

  const { data: catalogSummary, isLoading: catalogSummaryLoading } = useQuery({
    queryKey: ["catalog-summary"],
    queryFn: getCatalogSummary,
    enabled: canRenderProtectedContent,
  });

  const { data: progress = [], isFetched: progressFetched } = useQuery({
    queryKey: ["user-progress"],
    queryFn: listUserProgress,
    enabled: canRenderProtectedContent,
  });

  const genres = useMemo(() => {
    return Object.keys(TAG_FILTER_DICTIONARY).map((value) => ({
      value,
      label: getDictionaryLabel(TAG_FILTER_DICTIONARY, value, language, value),
    }));
  }, [language]);
  const genreOptionByValue = useMemo(
    () => new Map(genres.map((option) => [option.value, option])),
    [genres]
  );
  const tagGroups = useMemo(() => {
    const groups = TAG_FILTER_GROUPS.map((group) => ({
      ...group,
      label: language === "en" ? group.label.en : group.label.ja,
      options: group.tags
        .map((value) => genreOptionByValue.get(value))
        .filter(Boolean),
    })).filter((group) => group.options.length > 0);

    const groupedValues = new Set(TAG_FILTER_GROUPS.flatMap((group) => group.tags));
    const ungroupedOptions = genres.filter(
      (option) => option.value !== "all" && !groupedValues.has(option.value)
    );

    if (ungroupedOptions.length > 0) {
      groups.push({
        id: "other",
        label: language === "en" ? "Other" : "その他",
        options: ungroupedOptions,
      });
    }

    return groups;
  }, [genreOptionByValue, genres, language]);

  const seriesLikeCategoryOptions = useMemo(() => {
    const orderedKeys = SERIES_FILTER_ORDER.filter((key) => key in SERIES_FILTER_DICTIONARY);
    const orderedKeySet = new Set(orderedKeys);
    const leftoverKeys = Object.keys(SERIES_FILTER_DICTIONARY).filter((key) => !orderedKeySet.has(key));
    const keys = [...orderedKeys, ...leftoverKeys].filter((key) => !SERIES_FILTER_HIDDEN.has(String(key)));

    return keys.map((value) => ({
      value,
      label: getDictionaryLabel(SERIES_FILTER_DICTIONARY, value, language, `Category ${value}`),
    }));
  }, [language]);
  const categoryCardOptions = useMemo(
    () => seriesLikeCategoryOptions.filter((option) => option.value !== "all"),
    [seriesLikeCategoryOptions]
  );
  const selectedCategoryOption = categoryCardOptions.find((o) => o.value === seriesLikeCategoryFilter) ?? null;
  const shouldLoadVideoGrid = filterMode === "tag" || Boolean(selectedCategoryOption);
  const videoQueryParams = useMemo(() => {
    const params = {
      page: currentPage,
      pageSize: CATALOG_PAGE_SIZE,
      view: "card",
    };

    if (filterMode === "category" && selectedCategoryOption) {
      params.categoryId = seriesLikeCategoryFilter;
      // 並びはカテゴリ内の `sort_order` のみ。既定は降順。
      params.sort = categoryOrder === "asc" ? "order_asc" : "order_desc";
    }

    if (filterMode === "tag") {
      params.sort = sort;
      params.groupByCategory = "1";
      if (tagCategoryFilter !== "all") {
        params.categoryId = tagCategoryFilter;
      }
      if (selectedTagFilters.length > 0) {
        params.tags = selectedTagFilters.join(",");
      }
      if (showUnwatched) {
        params.unwatched = "1";
      }
      if (showWatched) {
        params.watched = "1";
      }
    }

    return params;
  }, [categoryOrder, currentPage, filterMode, selectedCategoryOption, selectedTagFilters, seriesLikeCategoryFilter, showUnwatched, showWatched, sort, tagCategoryFilter]);

  const usesClientProgressFilter = filterMode === "tag" && (showUnwatched || showWatched);
  const progressFilterKey = useMemo(
    () => progress.map((item) => `${item.video_id}:${item.is_completed ? 1 : 0}`).join("|"),
    [progress]
  );

  const { data: videoResponse, isLoading: rawVideosLoading } = useQuery({
    queryKey: ["videos", "catalog", videoQueryParams, usesClientProgressFilter ? progressFilterKey : ""],
    queryFn: async () => {
      if (!usesClientProgressFilter) {
        return listVideos(videoQueryParams);
      }

      const clientParams = {
        ...videoQueryParams,
        page: 1,
        pageSize: 100,
      };
      delete clientParams.unwatched;
      delete clientParams.watched;

      const response = await listAllVideos(clientParams);
      const completedVideoIds = new Set(
        progress
          .filter((item) => item.is_completed === true)
          .map((item) => String(item.video_id))
      );
      const filteredItems = response.items.filter((video) => {
        const isCompleted = completedVideoIds.has(String(video.id));
        if (showWatched && !isCompleted) return false;
        if (showUnwatched && isCompleted) return false;

        if (selectedTagFilters.length === 0) return true;

        const tagSlugs = new Set(getVideoTagSlugs(video));
        return selectedTagFilters.every((tag) => tagSlugs.has(tag));
      });
      const startIndex = (currentPage - 1) * CATALOG_PAGE_SIZE;

      return {
        items: filteredItems.slice(startIndex, startIndex + CATALOG_PAGE_SIZE),
        pagination: {
          page: currentPage,
          pageSize: CATALOG_PAGE_SIZE,
          total: filteredItems.length,
        },
      };
    },
    enabled: canRenderProtectedContent && shouldLoadVideoGrid && (!usesClientProgressFilter || progressFetched),
    placeholderData: keepPreviousData,
  });

  const videosLoading = rawVideosLoading || (usesClientProgressFilter && !progressFetched);
  const videos = videoResponse?.items ?? [];

  const isCategoryOrderDescending = categoryOrder === "desc";
  const sortOptions = SORT_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));
  const progressByVideoId = useMemo(
    () => new Map(progress.map((item) => [String(item.video_id), item])),
    [progress]
  );
  const categoryCounts = useMemo(
    () => new Map((catalogSummary?.categories ?? []).map((item) => [String(getSummaryCategoryId(item) ?? ""), item.total])),
    [catalogSummary]
  );
  const tagCounts = useMemo(
    () => new Map((catalogSummary?.tags ?? []).map((item) => [String(item.slug ?? ""), item.total])),
    [catalogSummary]
  );
  const visibleTagGroups = useMemo(() => {
    if (catalogSummaryLoading) return tagGroups;

    return tagGroups
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => (tagCounts.get(option.value) ?? 0) > 0),
      }))
      .filter((group) => group.options.length > 0);
  }, [catalogSummaryLoading, tagCounts, tagGroups]);
  const categoryCoverById = useMemo(
    () => new Map((catalogSummary?.categories ?? []).map((item) => [String(getSummaryCategoryId(item) ?? ""), item.cover]).filter(([, cover]) => cover)),
    [catalogSummary]
  );
  const catalogTotal = catalogSummary?.total ?? 0;
  const totalVideoCount = videoResponse?.pagination?.total ?? videos.length;
  const totalPages = Math.max(1, Math.ceil(totalVideoCount / CATALOG_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const videoLayoutKey = videos.map((video) => video.id).join(",");

  useEffect(() => {
    if (!hasMountedFilterResetRef.current) {
      hasMountedFilterResetRef.current = true;
      return;
    }
    if (isRestoringFiltersRef.current) {
      // URL から復元した直後は、ページ番号も URL の値をそのまま使う。
      isRestoringFiltersRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [filterMode, seriesLikeCategoryFilter, tagCategoryFilter, selectedTagFilters, sort, categoryOrder, showUnwatched, showWatched]);

  useEffect(() => {
    if (!shouldLoadVideoGrid || videosLoading) return;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, shouldLoadVideoGrid, totalPages, videosLoading]);

  const nextSearch = useMemo(
    () =>
      buildCatalogSearch({
        filterMode,
        seriesLikeCategoryFilter,
        tagCategoryFilter,
        selectedTagFilters,
        sort,
        categoryOrder,
        showUnwatched,
        showWatched,
        currentPage,
      }),
    [categoryOrder, currentPage, filterMode, selectedTagFilters, seriesLikeCategoryFilter, showUnwatched, showWatched, sort, tagCategoryFilter]
  );

  // フィルタの状態を URL へ反映する。カテゴリの出入りだけは履歴に積み、
  // カテゴリページからブラウザバックでカテゴリ一覧へ戻れるようにする。
  useEffect(() => {
    // カテゴリを開いたときだけ履歴に積む。他のフィルタ操作で履歴が増えないようにする。
    const isEnteringCategory =
      seriesLikeCategoryFilter !== previousCategoryRef.current && seriesLikeCategoryFilter !== "all";
    previousCategoryRef.current = seriesLikeCategoryFilter;
    writtenSearchRef.current = nextSearch;
    if (seriesLikeCategoryFilter === "all") hasPushedCategoryEntryRef.current = false;

    const currentSearch = searchParams.toString();
    if (nextSearch === currentSearch) return;

    if (isEnteringCategory) hasPushedCategoryEntryRef.current = true;
    // useSearchParams の setter は URL が変わるたびに関数が作り直され、
    // ブラウザバック直後にこの effect が古い状態で再実行されてしまうため navigate を使う。
    navigate({ search: nextSearch }, { replace: !isEnteringCategory });
    // searchParams は「今の URL」を読むためだけに参照しており、依存に入れると
    // URL 更新のたびにこの effect が再実行されてしまうため意図的に除外する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, nextSearch, seriesLikeCategoryFilter]);

  // ブラウザバック / フォワードなど、こちら以外の要因で URL が変わったら状態を戻す。
  useEffect(() => {
    const currentSearch = searchParams.toString();
    if (currentSearch === writtenSearchRef.current) return;
    writtenSearchRef.current = currentSearch;

    const filters = readCatalogFilters(searchParams);
    // 復元時は常に新しい配列 / 値を渡すので、ページ番号リセットの effect が必ず走り、
    // このフラグはそこで消費される。
    isRestoringFiltersRef.current = true;
    setFilterMode(filters.filterMode);
    setSeriesLikeCategoryFilter(filters.seriesLikeCategoryFilter);
    setTagCategoryFilter(filters.tagCategoryFilter);
    setSelectedTagFilters(filters.selectedTagFilters);
    setSort(filters.sort);
    setCategoryOrder(filters.categoryOrder);
    setShowUnwatched(filters.showUnwatched);
    setShowWatched(filters.showWatched);
    setCurrentPage(filters.currentPage);
  }, [searchParams]);

  // カテゴリページからカテゴリ一覧へ戻す。自前で積んだ履歴があればそれを戻し、
  // 直接アクセスなどで履歴が無い場合はフィルタを解除するだけにする。
  const handleBackToCategoryList = () => {
    if (hasPushedCategoryEntryRef.current) {
      hasPushedCategoryEntryRef.current = false;
      navigate(-1);
      return;
    }
    setSeriesLikeCategoryFilter("all");
  };

  useEffect(() => {
    const element = videoColumnRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;

    const updateHeight = () => {
      const measuredHeight = Math.ceil(element.getBoundingClientRect().height);
      const videoGrid = element.querySelector("[data-video-grid]");
      const firstVideoCard = element.querySelector("[data-video-grid-card]");
      const videoCardHeight = firstVideoCard ? Math.ceil(firstVideoCard.getBoundingClientRect().height) : 0;
      const videoGridWidth = videoGrid ? videoGrid.getBoundingClientRect().width : 0;
      const estimatedThumbnailHeight = videoGridWidth > 0 ? ((videoGridWidth - 32) / 3) * (9 / 16) : 0;
      const rowHeight = videoCardHeight || estimatedThumbnailHeight;
      const twoRowsMinHeight = rowHeight > 0 ? rowHeight * 2 + 16 : 0;
      const nextHeight = Math.max(measuredHeight, twoRowsMinHeight);
      setTagPanelMaxHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
    };
    const scheduleUpdate = () => {
      requestAnimationFrame(updateHeight);
      window.setTimeout(updateHeight, 120);
    };

    scheduleUpdate();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [filterMode, currentPage, videoLayoutKey, totalVideoCount, videosLoading, selectedTagFilters.length, tagCategoryFilter, sort, showUnwatched, showWatched]);

  if (isLoadingAuth || !canRenderProtectedContent) {
    return null;
  }

  const selectedSeriesLabel =
    seriesLikeCategoryOptions.find((o) => o.value === seriesLikeCategoryFilter)?.label ??
    getDictionaryLabel(SERIES_FILTER_DICTIONARY, "all", language);
  const selectedTagCategoryLabel =
    seriesLikeCategoryOptions.find((o) => o.value === tagCategoryFilter)?.label ??
    getDictionaryLabel(SERIES_FILTER_DICTIONARY, "all", language);
  const selectedTagLabel = selectedTagFilters.length > 0
    ? selectedTagFilters
        .map((value) => genreOptionByValue.get(value)?.label ?? value)
        .join("、")
    : getDictionaryLabel(TAG_FILTER_DICTIONARY, "all", language);
  const selectedTagOptions = selectedTagFilters.map((value) => ({
    value,
    label: genreOptionByValue.get(value)?.label ?? value,
  }));
  const selectedSortLabel = sortOptions.find((o) => o.value === sort)?.label ?? t("newest");
  const activeTagFilterCount =
    selectedTagFilters.length +
    (tagCategoryFilter !== "all" ? 1 : 0) +
    (showUnwatched ? 1 : 0) +
    (showWatched ? 1 : 0);
  const toggleTagFilter = (value) => {
    setSelectedTagFilters((current) =>
      current.includes(value)
        ? current.filter((tag) => tag !== value)
        : current.concat(value)
    );
  };

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(1, page), totalPages);
    if (nextPage === safeCurrentPage) return;

    setCurrentPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const VideoPagination = () => {
    if (!shouldLoadVideoGrid || videosLoading || totalVideoCount <= CATALOG_PAGE_SIZE) return null;

    return (
      <div className="mt-6 flex flex-col items-center gap-2">
        <p className="text-xs text-[#6b5540]">
          {totalVideoCount}本中 {(safeCurrentPage - 1) * CATALOG_PAGE_SIZE + 1}-
          {Math.min(safeCurrentPage * CATALOG_PAGE_SIZE, totalVideoCount)}本を表示
        </p>
        <Pagination>
          <PaginationContent className="flex-wrap justify-center gap-2">
            <PaginationItem>
              <PaginationLink
                href="#"
                size="default"
                onClick={(event) => {
                  event.preventDefault();
                  goToPage(safeCurrentPage - 1);
                }}
                className={`gap-1 pl-2.5 ${safeCurrentPage === 1 ? "pointer-events-none opacity-50" : ""}`}
              >
                <ChevronLeft className="h-4 w-4" />
                <span>前へ</span>
              </PaginationLink>
            </PaginationItem>
            <PaginationItem className="px-2 text-sm font-medium text-[#5d4529] md:hidden">
              {safeCurrentPage}/{totalPages}
            </PaginationItem>
            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;
              return (
                <PaginationItem key={page} className="hidden md:list-item">
                  <PaginationLink
                    href="#"
                    isActive={page === safeCurrentPage}
                    onClick={(event) => {
                      event.preventDefault();
                      goToPage(page);
                    }}
                    className={page === safeCurrentPage ? "border-[#d4c4b0] text-[#2d1810]" : "text-[#5d4529]"}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationLink
                href="#"
                size="default"
                onClick={(event) => {
                  event.preventDefault();
                  goToPage(safeCurrentPage + 1);
                }}
                className={`gap-1 pr-2.5 ${safeCurrentPage === totalPages ? "pointer-events-none opacity-50" : ""}`}
              >
                <span>次へ</span>
                <ChevronRight className="h-4 w-4" />
              </PaginationLink>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  const VideoGrid = ({ videos: displayVideos }) => (
    <motion.div
      data-video-grid
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid grid-cols-1 md:grid-cols-3 gap-4"
    >
      {videosLoading ? (
        [...Array(CATALOG_PAGE_SIZE)].map((_, i) => (
          <div key={i} data-video-grid-card className="space-y-3">
            <Skeleton className="aspect-video rounded-xl bg-[#d4c4b0]" />
            <Skeleton className="h-4 w-3/4 bg-[#d4c4b0]" />
            <Skeleton className="h-3 w-1/2 bg-[#d4c4b0]" />
          </div>
        ))
      ) : displayVideos.length === 0 ? (
        <div className="col-span-full text-center py-20 text-[#6b5540]">{t("noResults")}</div>
      ) : (
        displayVideos.map((video, index) => (
          <motion.div
            key={video.id}
            data-video-grid-card
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
          >
            <VideoCard
              video={video}
              progress={progressByVideoId.get(String(video.id))}
            />
          </motion.div>
        ))
      )}
    </motion.div>
  );

  const CategoryContent = () => (
    <div className="space-y-4">
      {selectedCategoryOption ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Badge className="mb-2 bg-[#8b6f47] text-[#f5ede3]">
                カテゴリ: {selectedSeriesLabel}
              </Badge>
              <h2 className="text-xl font-bold text-[#2d1810]">{selectedSeriesLabel}</h2>
              <p className="mt-1 text-sm text-[#6b5540]">
                このカテゴリ内の動画を表示順の{isCategoryOrderDescending ? "降順" : "昇順"}で表示しています。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCategoryOrder((current) => (current === "asc" ? "desc" : "asc"))}
                className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
                aria-label={isCategoryOrderDescending ? "昇順に切り替える" : "降順に切り替える"}
              >
                {isCategoryOrderDescending ? (
                  <ArrowDownWideNarrow className="h-4 w-4 text-[#8b6f47]" />
                ) : (
                  <ArrowUpWideNarrow className="h-4 w-4 text-[#8b6f47]" />
                )}
                {isCategoryOrderDescending ? "降順" : "昇順"}
              </Button>
              <Button
                variant="outline"
                onClick={handleBackToCategoryList}
                className="border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
              >
                カテゴリ一覧に戻る
              </Button>
            </div>
          </div>
          <VideoGrid videos={videos} />
          <VideoPagination />
        </>
      ) : (
        <>
          <div>
            <h2 className="text-lg font-bold text-[#2d1810]">カテゴリ一覧</h2>
            <p className="mt-1 text-sm text-[#6b5540]">
              カテゴリを選ぶと、その中の動画が表示順（既定は降順）で表示されます。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoryCardOptions.map((option) => {
          const count = option.value === "all"
            ? catalogTotal
            : categoryCounts.get(String(option.value)) ?? 0;
              const coverVideo = categoryCoverById.get(String(option.value));

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setSeriesLikeCategoryFilter(option.value)}
                  className="group overflow-hidden rounded-2xl border border-[#e8dccd] bg-white text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#d4c4b0] hover:shadow-md"
            >
                  <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[#f0e3d3] to-[#d6c0a0]">
                    {coverVideo?.thumbnail_url ? (
                      <img
                        src={coverVideo.thumbnail_url}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-sm font-medium text-[#6b5540]">
                        No Thumbnail
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2d1810]/80 via-[#2d1810]/10 to-transparent" />
                    <Badge className="absolute bottom-3 left-3 bg-white/90 text-[#5d4529]">
                      {catalogSummaryLoading ? "..." : `${count}本`}
                    </Badge>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-[#2d1810] transition-colors group-hover:text-[#8b6f47]">
                      {option.label}
                    </h3>
                    <p className="mt-1 text-xs text-[#6b5540]">クリックして動画を見る</p>
                  </div>
            </button>
          );
        })}
          </div>
        </>
      )}
    </div>
  );

  const TagFilterContent = () => (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
        <div className="min-w-0">
          <label className="text-sm font-medium text-[#2d1810] mb-2 block">{t("sort")}</label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-full bg-[#f5ede3]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-0">
          <label className="text-sm font-medium text-[#2d1810] mb-2 block">{t("filter")}</label>
          <div className="flex w-full gap-2">
            <Button
              variant={showUnwatched ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setShowUnwatched((current) => !current);
                setShowWatched(false);
              }}
              className={`h-9 flex-1 ${
                showUnwatched ? "bg-[#8b6f47] text-[#f5ede3]" : "text-[#5d4529]"
              }`}
            >
              {t("unwatched")}
            </Button>
            <Button
              variant={showWatched ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setShowWatched((current) => !current);
                setShowUnwatched(false);
              }}
              className={`h-9 flex-1 ${
                showWatched ? "bg-[#8b6f47] text-[#f5ede3]" : "text-[#5d4529]"
              }`}
            >
              {t("watchedDone")}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <label className="text-sm font-medium text-[#2d1810]">タグ</label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setSelectedTagFilters([])}
            disabled={selectedTagFilters.length === 0}
            className="h-7 px-2 text-xs text-[#6b5540] hover:bg-[#f5ede3] hover:text-[#2d1810] disabled:opacity-40"
          >
            リセット
          </Button>
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          <Badge
            variant={selectedTagFilters.length === 0 ? "default" : "outline"}
            className={`max-w-full cursor-pointer whitespace-normal break-keep transition-all ${
              selectedTagFilters.length === 0
                ? "bg-[#8b6f47] text-[#f5ede3]"
                : "text-[#5d4529] hover:bg-[#f5ede3]"
            }`}
            onClick={() => setSelectedTagFilters([])}
          >
            {genreOptionByValue.get("all")?.label ?? "すべて"}
            <span className="ml-1 text-[10px] opacity-80">{catalogTotal}</span>
          </Badge>
        </div>
        <div className="mb-5 rounded-xl border border-[#e8dccd] bg-white p-3">
          <h4 className="mb-2 text-xs font-semibold text-[#6b5540]">カテゴリで絞り込み</h4>
          <div className="flex flex-wrap gap-2">
            {seriesLikeCategoryOptions.map((category) => {
              const isSelected = tagCategoryFilter === category.value;
              const count = category.value === "all"
                ? catalogTotal
                : categoryCounts.get(String(category.value)) ?? 0;

              return (
                <Badge
                  key={category.value}
                  variant={isSelected ? "default" : "outline"}
                  className={`max-w-full cursor-pointer whitespace-normal break-keep text-left transition-all ${
                    isSelected
                      ? "bg-[#8b6f47] text-[#f5ede3]"
                      : "text-[#5d4529] hover:bg-[#f5ede3]"
                  }`}
                  onClick={() => setTagCategoryFilter(category.value)}
                >
                  {category.label}
                  <span className="ml-1 text-[10px] opacity-70">{count}</span>
                </Badge>
              );
            })}
          </div>
        </div>
        <div className="space-y-5">
          {visibleTagGroups.map((group) => (
            <div key={group.id} className="rounded-xl border border-[#e8dccd] bg-[#fffaf5] p-3">
              <h4 className="mb-2 text-xs font-semibold text-[#6b5540]">{group.label}</h4>
              <div className="flex flex-wrap gap-2">
                {group.options.map((g) => {
                  const isSelected = selectedTagFilters.includes(g.value);

                  return (
                    <Badge
                      key={g.value}
                      variant={isSelected ? "default" : "outline"}
                      className={`max-w-full cursor-pointer whitespace-normal break-keep text-left transition-all ${
                        isSelected
                          ? "bg-[#8b6f47] text-[#f5ede3]"
                          : "text-[#5d4529] hover:bg-[#f5ede3]"
                      }`}
                      onClick={() => toggleTagFilter(g.value)}
                    >
                      {g.label}
                      <span className="ml-1 text-[10px] opacity-70">
                        {catalogSummaryLoading ? "..." : (tagCounts.get(g.value) ?? 0)}
                      </span>
                    </Badge>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf6f1] via-[#f5ede3] to-[#efe5d8] text-[#2d1810] pb-20">
      <div className="w-full max-w-[1920px] mx-auto px-3 sm:px-4 lg:px-11 2xl:px-12 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#2d1810]">{t("catalog")}</h1>
        </motion.div>

        <div className="space-y-6">
          <Tabs value={filterMode} onValueChange={setFilterMode} className="rounded-2xl border border-[#e8dccd] bg-white p-4 md:p-5">
            <TabsList className="grid h-auto w-full grid-cols-2 bg-[#f5ede3] p-1">
              <TabsTrigger value="category" className="gap-2 data-[state=active]:bg-white data-[state=active]:text-[#2d1810]">
                <FolderTree className="w-4 h-4" />
                カテゴリ
              </TabsTrigger>
              <TabsTrigger value="tag" className="gap-2 data-[state=active]:bg-white data-[state=active]:text-[#2d1810]">
                <Tags className="w-4 h-4" />
                タグ絞り込み
              </TabsTrigger>
            </TabsList>
            <TabsContent value="category" className="mt-5">
              <CategoryContent />
            </TabsContent>
            <TabsContent value="tag" className="mt-5">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
                <motion.aside
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="w-full min-w-0 lg:w-72 lg:flex-shrink-0"
                >
                  <div className="sticky top-[72px] z-30 lg:hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setIsTagFilterOpen((open) => {
                          if (open) {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                          return !open;
                        });
                      }}
                      className="flex w-full items-center justify-between rounded-xl border border-[#e8dccd] bg-white/95 px-4 py-3 text-left font-semibold text-[#2d1810] shadow-sm backdrop-blur"
                      aria-expanded={isTagFilterOpen}
                    >
                      <span className="flex items-center gap-2">
                        <Tags className="w-4 h-4 text-[#8b6f47]" />
                        タグ絞り込み
                        {activeTagFilterCount > 0 && (
                          <Badge className="bg-[#8b6f47] text-[#f5ede3]">
                            {activeTagFilterCount}
                          </Badge>
                        )}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-[#8b6f47] transition-transform ${
                          isTagFilterOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                  <div
                    className={`${isTagFilterOpen ? "mt-3 block" : "hidden"} rounded-xl border border-[#e8dccd] bg-white/95 p-3 transition-[max-height] duration-200 lg:sticky lg:top-8 lg:mt-0 lg:block lg:max-h-[var(--tag-panel-max-height)] lg:overflow-y-auto lg:overscroll-contain lg:border-0 lg:bg-white lg:p-0 lg:pr-3`}
                    style={tagPanelMaxHeight ? { "--tag-panel-max-height": `${tagPanelMaxHeight}px` } : undefined}
                  >
                    <h3 className="mb-4 hidden items-center gap-2 font-semibold text-[#2d1810] lg:flex">
                      <Tags className="w-4 h-4 text-[#8b6f47]" />
                      タグ絞り込み
                    </h3>
                    <TagFilterContent />
                  </div>
                </motion.aside>

                <div ref={videoColumnRef} className="flex-1 min-w-0">
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="max-w-full whitespace-normal break-keep border-[#d4c4b0] px-3 py-1.5 text-xs text-[#5d4529]">
                      カテゴリ: {selectedTagCategoryLabel}
                    </Badge>
                    {selectedTagOptions.length > 0 ? (
                      <>
                        <Badge className="bg-[#8b6f47] px-3 py-1.5 text-xs text-[#f5ede3]">タグ</Badge>
                        {selectedTagOptions.map((tag) => (
                          <Badge
                            key={tag.value}
                            role="button"
                            tabIndex={0}
                            variant="outline"
                            className="max-w-full cursor-pointer whitespace-normal break-keep border-[#d4c4b0] px-3 py-1.5 text-xs text-[#5d4529] transition-colors hover:bg-[#f5ede3]"
                            onClick={() => toggleTagFilter(tag.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                toggleTagFilter(tag.value);
                              }
                            }}
                            title={`${tag.label} を解除`}
                          >
                            {tag.label}
                            <span className="ml-1.5 text-sm leading-none" aria-hidden>×</span>
                          </Badge>
                        ))}
                      </>
                    ) : (
                      <Badge className="max-w-full whitespace-normal break-keep bg-[#8b6f47] px-3 py-1.5 text-xs text-[#f5ede3]">
                        タグ: {selectedTagLabel}
                      </Badge>
                    )}
                    <Badge variant="outline" className="max-w-full whitespace-normal break-keep border-[#d4c4b0] px-3 py-1.5 text-xs text-[#5d4529]">
                      {t("sort")}: {selectedSortLabel}
                    </Badge>
                    {showUnwatched && (
                      <Badge variant="outline" className="border-[#d4c4b0] px-3 py-1.5 text-xs text-[#5d4529]">
                        {t("unwatched")}
                      </Badge>
                    )}
                    {showWatched && (
                      <Badge variant="outline" className="border-[#d4c4b0] px-3 py-1.5 text-xs text-[#5d4529]">
                        {t("watchedDone")}
                      </Badge>
                    )}
                  </div>
                  <VideoGrid videos={videos} />
                  <VideoPagination />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
